import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Search as SearchIcon, X, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { searchImages, fetchProjectsByStatus } from "@/api";
import ImageCard from "@/components/ImageCard";
import ImageDetailDialog from "@/components/ImageDetailDialog";
import { cn, prettify } from "@/lib/utils";

const ROOM_TYPES = [
  "kitchen",
  "living_room",
  "bedroom",
  "bathroom",
  "dining_room",
  "office",
  "outdoor",
  "hallway",
];

const STYLES = [
  "modern",
  "minimalist",
  "mid_century",
  "industrial",
  "scandinavian",
  "contemporary",
  "traditional",
  "art_deco",
  "bohemian",
  "rustic",
  "coastal",
  "japandi",
];

const CATEGORIES = [
  "inspiration",
  "site",
  "product",
  "material",
  "floorplan",
];

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    project_id: "",
    room: "",
    style: "",
    category: "",
  });
  const [projects, setProjects] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProjectsByStatus("all").then(setProjects).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q: q || undefined };
        Object.entries(filters).forEach(([k, v]) => {
          if (v) params[k] = v;
        });
        const res = await searchImages(params);
        setResults(res.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, filters]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  function clearFilters() {
    setFilters({ project_id: "", room: "", style: "", category: "" });
  }

  return (
    <div
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 md:py-14"
      data-testid="search-page"
    >
      <div className="mb-8 md:mb-12">
        <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-2">
          Library
        </p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-stone-900">
          Search every reference
        </h1>
        <p className="mt-3 text-stone-500 max-w-lg">
          Across every project — sift by room, style, material, or a single
          word. Your visual memory, instantly searchable.
        </p>
      </div>

      <div className="sticky top-16 z-30 -mx-6 md:-mx-10 px-6 md:px-10 pb-4 pt-4 bg-white/85 backdrop-blur-md border-b border-stone-100 mb-8">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <SearchIcon
              className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
              strokeWidth={1.5}
            />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tags, materials, notes…"
              className="pl-11 pr-10 h-12 rounded-full border-stone-200 bg-stone-50 focus-visible:bg-white"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                data-testid="search-clear-button"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-stone-200 flex items-center justify-center text-stone-500"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>
          <Button
            type="button"
            onClick={() => setShowFilters((s) => !s)}
            data-testid="toggle-filters-button"
            className={cn(
              "rounded-full h-12 px-5 shrink-0 font-medium",
              showFilters || activeFilterCount > 0
                ? "bg-stone-900 text-white hover:bg-stone-800"
                : "bg-stone-100 text-stone-900 hover:bg-stone-200"
            )}
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-stone-900 text-[10px] font-semibold">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        {showFilters && (
          <div
            className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3"
            data-testid="filters-panel"
          >
            <FilterChipGroup
              label="Project"
              testid="filter-project"
              value={filters.project_id}
              onChange={(v) => setFilters({ ...filters, project_id: v })}
              options={projects.map((p) => ({ value: p.id, label: p.name }))}
            />
            <FilterChipGroup
              label="Room"
              testid="filter-room"
              value={filters.room}
              onChange={(v) => setFilters({ ...filters, room: v })}
              options={ROOM_TYPES.map((r) => ({ value: r, label: prettify(r) }))}
            />
            <FilterChipGroup
              label="Style"
              testid="filter-style"
              value={filters.style}
              onChange={(v) => setFilters({ ...filters, style: v })}
              options={STYLES.map((s) => ({ value: s, label: prettify(s) }))}
            />
            <FilterChipGroup
              label="Category"
              testid="filter-category"
              value={filters.category}
              onChange={(v) => setFilters({ ...filters, category: v })}
              options={CATEGORIES.map((c) => ({ value: c, label: prettify(c) }))}
            />
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                data-testid="clear-filters-button"
                className="col-span-2 md:col-span-4 justify-self-end text-xs uppercase tracking-[0.15em] font-semibold text-stone-500 hover:text-stone-900"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-6">
        <p className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">
          {loading
            ? "Searching…"
            : `${results.length} ${
                results.length === 1 ? "result" : "results"
              }`}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-stone-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Searching…
        </div>
      ) : results.length === 0 ? (
        <div
          className="border border-dashed border-stone-200 rounded-2xl py-20 text-center"
          data-testid="search-empty-state"
        >
          <div className="w-14 h-14 rounded-full bg-stone-100 mx-auto flex items-center justify-center mb-5">
            <SearchIcon className="w-6 h-6 text-stone-400" strokeWidth={1.5} />
          </div>
          <h3 className="font-display text-2xl text-stone-900">
            Nothing matches yet
          </h3>
          <p className="text-stone-500 mt-2 max-w-md mx-auto text-sm">
            Try a different word or loosen a filter — your library grows with
            every upload.
          </p>
        </div>
      ) : (
        <motion.div
          initial={false}
          className="columns-2 md:columns-3 lg:columns-4 gap-4"
          data-testid="search-results-grid"
        >
          {results.map((img, i) => (
            <ImageCard
              key={img.id}
              image={img}
              index={i}
              testidPrefix="search-result"
              onClick={() => setSelected(img)}
            />
          ))}
        </motion.div>
      )}

      <ImageDetailDialog
        image={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        onUpdated={(updated) => {
          setResults((arr) =>
            arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x))
          );
          setSelected(updated);
        }}
      />
    </div>
  );
}

function FilterChipGroup({ label, testid, value, onChange, options }) {
  return (
    <div data-testid={`${testid}-group`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-stone-500 mb-2">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              data-testid={`${testid}-${opt.value}`}
              onClick={() => onChange(active ? "" : opt.value)}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium transition-colors",
                active
                  ? "bg-stone-900 text-white"
                  : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
